import { useEffect, useRef, useState } from 'react';
import { Analytics } from '../analytics';
import { useChatLocale } from '../context/ChatThemeContext';
import type { DynamicQuestionItem } from '../utils/content/dynamicQuestions';
import { logger } from '../utils/core/logger';
import { getSlideUpRevealStyles } from '../utils/ui/animationUtils';
import ArrowRightIcon from './ArrowRightIcon';

interface DynamicQuestionButtonProps {
    question: DynamicQuestionItem;
    show?: boolean;
    showArrow?: boolean;
    onDynamicQuestionClick?: (question: string) => void;
}

const DynamicQuestionButton: React.FC<DynamicQuestionButtonProps> = ({
    question,
    show = true,
    showArrow = true,
    onDynamicQuestionClick,
}) => {
    const [hasShownOnce, setHasShownOnce] = useState(false);
    const [showShimmer, setShowShimmer] = useState(false);
    const [isRevealed, setIsRevealed] = useState(false);
    const containerRef = useRef<HTMLDivElement | null>(null);
    const userLocale = useChatLocale();
    const isValid = Boolean(question.text);

    useEffect(() => {
        if (show && isValid && !hasShownOnce) {
            setHasShownOnce(true);
        }
    }, [show, isValid, hasShownOnce]);

    useEffect(() => {
        if (show && isValid) {
            const id = requestAnimationFrame(() => {
                void containerRef.current?.offsetHeight;
                setIsRevealed(true);
            });
            return () => cancelAnimationFrame(id);
        }

        setIsRevealed(false);
    }, [show, isValid]);

    useEffect(() => {
        if (hasShownOnce) {
            const timer = setTimeout(() => {
                setShowShimmer(true);
                setTimeout(() => setShowShimmer(false), 800);
            }, 600);
            return () => clearTimeout(timer);
        }

        setShowShimmer(false);
    }, [hasShownOnce]);

    const handleClick = () => {
        logger.debug('🖱️ DynamicQuestionButton clicked', {
            question: question.text,
        });

        const questionClickKey = `dynamic_question_clicked_${question.text}`;
        const hasTrackedThisQuestionClick = sessionStorage.getItem(questionClickKey);

        if (!hasTrackedThisQuestionClick) {
            Analytics.trackDynamicQuestionClicked({
                questionText: question.text,
                language: userLocale,
            });
            sessionStorage.setItem(questionClickKey, 'true');
        }

        if (onDynamicQuestionClick) {
            onDynamicQuestionClick(question.text);
        } else {
            logger.warn('⚠️ Dynamic question clicked but no callback provided');
        }
    };

    if (!isValid) {
        return null;
    }

    const revealStyles = getSlideUpRevealStyles({ show: isRevealed, transitionDelay: '0s' });

    return (
        <div
            ref={containerRef}
            className="inboundx-question-container"
            style={{
                ...revealStyles,
                transform: isRevealed ? 'none' : revealStyles.transform,
                willChange: isRevealed ? 'auto' : 'transform, opacity',
                visibility: isRevealed ? 'visible' : 'hidden',
                pointerEvents: show ? 'auto' : 'none',
            }}
        >
            <button
                type="button"
                className="inboundx-search-bar-button flex items-center gap-2 px-3 py-[9px] rounded-full outline-none no-underline transition-all duration-200 ease-in-out relative overflow-hidden cursor-pointer active:scale-90"
                onClick={handleClick}
                onMouseDown={(event) => event.preventDefault()}
                style={{
                    backgroundImage: 'linear-gradient(var(--rw-bg-chip), var(--rw-bg-chip))',
                    border: '1px solid var(--rw-border-chip)',
                    borderRadius: 'var(--rw-radius-pill)',
                    fontFamily: 'var(--rw-font-family)',
                }}
            >
                {showShimmer && (
                    <div
                        className="absolute inset-0 pointer-events-none"
                        style={{
                            background:
                                'linear-gradient(135deg, transparent 30%, rgba(255,255,255,0.25) 50%, transparent 80%)',
                            transform: 'translateX(-100%) translateY(-100%)',
                            animation: 'rw-diagonal-shimmer 0.8s ease-out forwards',
                            width: '200%',
                            height: '200%',
                        }}
                    />
                )}

                <span
                    className="font-normal leading-tight relative z-10"
                    style={{ color: 'var(--rw-text-primary)' }}
                >
                    {question.text}
                </span>
                {showArrow && (
                    <ArrowRightIcon
                        width={20}
                        height={20}
                        color="var(--rw-text-secondary)"
                        className="flex-none relative z-10"
                    />
                )}

                <style>{`
                    .inboundx-search-bar-button {
                        background-color: var(--rw-surface-panel);
                    }

                    .inboundx-search-bar-button:hover {
                        background-color: var(--rw-surface-interactive-hover);
                    }

                    .inboundx-search-bar-button:active {
                        background-color: var(--rw-surface-interactive-active);
                    }
                `}</style>
            </button>
        </div>
    );
};

export default DynamicQuestionButton;
